import React, { useState } from "react";
import { BiSolidHide } from "react-icons/bi";
import { LuSearch } from "react-icons/lu";
import useOutsideClick from "../../hooks/useOutsideClick";
import HideItem from "./hide-item";
import styles from "./hide-section.module.css";

/**
 * HideFieldSection Component
 * 
 * Manages column visibility toggle dropdown. Displays searchable list of datatable fields
 * with checkboxes to show/hide columns. Prevents hiding primary fields (name/tradeName).
 * Allows bulk hide/show all operations and search filtering of field list.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.dispatch - Dispatch function for visibility state updates
 * @param {Array} props.fields - Available datatable fields
 * @param {Array} props.hiddenFieldNames - Currently hidden field names
 * @returns {JSX.Element} Column visibility management dropdown interface
 */
const HideFieldSection = ({ props }) => {
  const { dispatch, fields, hiddenFieldNames } = props;

  const { ref, isOpen, toggleNav } = useOutsideClick();

  // get array of fields used in the datatable but remove the name field
  // because the name field cannot be hidden
  const modifiedFields = fields.filter(
    (item) =>
      !(item.actualName === "tradeName") && !(item.actualName === "name")
  );

  const push = (item) => {
    dispatch({
      stateArray: "hiddenFieldNames",
      type: "push_array",
      value: item,
    });
  };

  const remove = (item) => {
    dispatch({
      type: "unhide_field",
      value: item,
    });
  };

  const clear = () => {
    dispatch({ type: "unhide_all_fields" });
  };

  const handleHideAll = () => {
    dispatch({
      type: "hide_all_fields",
      value: modifiedFields.map((field) => field.actualName),
    });
  };

  const [searchValue, setSearchValue] = useState("");

  const handleOnChange = (e) => {
    setSearchValue(e.target.value);
  };

  return (
    <div ref={ref} className={"shutdownfilterdiv dropdown"}>
      <span
        className="shutdownfilterbuttons"
        hideapplied={hiddenFieldNames.length === 0 ? null : "true"}
        data-tip="Hide field"
        onClick={() => toggleNav()}
      >
        <span>
          <BiSolidHide size={"1.5rem"} />
        </span>
        {hiddenFieldNames.length === 0 && (
          <span className="desktop">Hide</span>
        )}
        {hiddenFieldNames.length === 1 && (
          <span className="desktop">1 hidden field</span>
        )}
        {hiddenFieldNames.length > 1 && (
          <span className="desktop">
            {[hiddenFieldNames.length, "hidden fields"].join(" ")}
          </span>
        )}
      </span>

      <div style={{ display: isOpen ? "block" : "none" }}>
        <div
          className={styles.menu}
          onClick={(e) => {
            // do not close modal if anything inside modal content is clicked
            e.stopPropagation();
          }}
        >
          <div
            className={styles.menuheader}
            style={{ color: "rgb(102, 102, 102)" }}
          >
            Hide
          </div>

          <div className={styles.hsection}></div>

          <div className={styles.itemselectiondiv}>
            <span className={styles.icondiv}>
              <LuSearch />
            </span>
            <input
              type="search"
              value={searchValue}
              className={styles.fieldsearchinput}
              placeholder="Search a field"
              onChange={(e) => handleOnChange(e)}
            ></input>
          </div>

          <div className={styles.menucontent}>
            {modifiedFields.map(
              (field, index) =>
                field.displayName
                  .toLowerCase()
                  .includes(searchValue.toLowerCase()) && (
                  <HideItem
                    key={index}
                    field={field}
                    props={props}
                    push={push}
                    remove={remove}
                  />
                )
            )}
          </div>

          <div
            className={styles.menufooter}
            style={{ color: "rgb(102, 102, 102)" }}
          >
            <div className={styles.footerbuttonswrapper}>
              <div
                className={styles.footerbutton}
                style={{ whiteSpace: "nowrap" }}
                onClick={() => handleHideAll()}
              >
                <span>Hide all</span>
              </div>

              <div
                className={styles.footerbutton}
                style={{ whiteSpace: "nowrap" }}
                onClick={() => clear()}
              >
                <span>Show all</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HideFieldSection;
